import { useCallback, useEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import { DRAG_THRESHOLD_PX, dragTarget } from '@/lib/slotBooking'

// ─── Dragging an appointment to a new time ─────────────────────────────────────
//
// Requested 2026-08-24 alongside the in-place reschedule ("add click and drag
// capability ... to make it easier to manage reschedules"). The gesture PROPOSES;
// the confirm dialog decides. Nothing in this file writes.
//
// The arithmetic is NOT here — `dragTarget` in lib/slotBooking.ts owns it, pure
// and unit-tested. This hook owns only the pointer plumbing, which is the half
// that cannot be tested without a real browser.
//
// WHY THE DROP OPENS A DIALOG RATHER THAN WRITING (operator's choice, 2026-08-24)
//   A day grid is a dense target and a mis-drag moves a real customer's
//   appointment. The dialog names the old time and the new one, so what is about
//   to happen is readable before it happens — and it doubles as the place to type
//   an exact time, which is the other half of the same request.
//
// VERTICAL ONLY. Resources are auto-selected server-side, so a tile crossing into
// another machine's column would promise an assignment `/reschedule` is free to
// ignore. See dragTarget.

/** Live state of an in-flight drag, for the tile to draw itself with. */
export interface TileDragState {
  dragging: boolean
  /** Vertical offset to apply to the tile, in pixels, already snapped to the grid. */
  offsetPixels: number
  /** The proposed start, minutes from midnight, or null when nothing is in flight. */
  targetMinutes: number | null
}

const IDLE: TileDragState = { dragging: false, offsetPixels: 0, targetMinutes: null }

export interface UseTileDragOptions {
  /**
   * Off entirely for a tile that cannot move — a customer's chat calendar, a
   * cancelled or completed appointment, one whose customer has arrived. A tile
   * that follows the pointer and then snaps back teaches the operator that the
   * grid lies; refusing the gesture is cheaper than explaining the 409.
   */
  enabled: boolean
  originalStartMinutes: number
  durationMinutes: number
  pixelsPerMinute: number
  intervalMinutes: number
  dayStartMinutes: number
  dayEndMinutes: number
  /** Called once, on release, and only when the proposal actually differs. */
  onDrop: (startMinutes: number) => void
}

/**
 * How long a FINGER must rest on a tile before the drag engages.
 *
 * Touch is the awkward case: the grid scrolls vertically, and a tile that grabbed
 * the pointer immediately would make the schedule unscrollable on a phone. So a
 * touch drag waits for a deliberate hold, and a scroll that starts first either
 * moves past the threshold or arrives as `pointercancel` — both abandon the
 * gesture. Mouse and pen have no such conflict and engage on movement alone.
 *
 * The consequence, stated rather than hidden: on a phone a drag is a best-effort
 * shortcut. The booking sheet's "Move to another time" is the reliable path there,
 * and it opens the same dialog.
 */
const TOUCH_HOLD_MS = 350

export function useTileDrag(options: UseTileDragOptions): {
  state: TileDragState
  /** Spread onto the tile element, together with onClickCapture. */
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void
  /**
   * Swallows the click a browser fires after a drag's pointerup — without it,
   * every drop would also open the booking sheet behind the dialog.
   */
  onClickCapture: (event: { preventDefault: () => void; stopPropagation: () => void }) => void
} {
  const [state, setState] = useState<TileDragState>(IDLE)

  // The gesture handlers live for the length of one drag and are attached to the
  // window, so they read their inputs from a ref rather than closing over the
  // render they were created in: the day reflows under an in-flight drag whenever
  // a refetch lands, and stale geometry would land the tile on the wrong slot.
  const latest = useRef(options)
  latest.current = options

  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const suppressClick = useRef(false)

  const clearHold = useCallback(() => {
    if (holdTimer.current !== null) {
      clearTimeout(holdTimer.current)
      holdTimer.current = null
    }
  }, [])

  // A pending hold timer must not outlive the tile — a refetch unmounts it.
  useEffect(() => clearHold, [clearHold])

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (!latest.current.enabled) return
    // Secondary buttons open context menus. `button` is 0 for touch.
    if (event.button !== 0) return

    const pointerId = event.pointerId
    const startY = event.clientY
    const isTouch = event.pointerType === 'touch'
    let engaged = false

    const resolve = (clientY: number) => dragTarget({
      originalStartMinutes: latest.current.originalStartMinutes,
      durationMinutes: latest.current.durationMinutes,
      deltaPixels: clientY - startY,
      pixelsPerMinute: latest.current.pixelsPerMinute,
      intervalMinutes: latest.current.intervalMinutes,
      dayStartMinutes: latest.current.dayStartMinutes,
      dayEndMinutes: latest.current.dayEndMinutes,
    })

    const paint = (clientY: number) => {
      const target = resolve(clientY)
      setState({
        dragging: true,
        // Drawn from the SNAPPED target, not the raw pointer delta, so the tile
        // sits on the grid line it would actually land on. A tile that tracks the
        // pointer continuously and jumps on release hides which slot was chosen
        // until it is too late to adjust.
        offsetPixels: (target.startMinutes - latest.current.originalStartMinutes)
          * latest.current.pixelsPerMinute,
        targetMinutes: target.startMinutes,
      })
    }

    const detach = () => {
      clearHold()
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onCancel)
      setState((prev) => (prev === IDLE ? prev : IDLE))
    }

    function onMove(moveEvent: PointerEvent) {
      if (moveEvent.pointerId !== pointerId) return
      const dy = moveEvent.clientY - startY
      if (!engaged) {
        // Before the hold fires, a moving finger is a scroll — let it go.
        if (isTouch) {
          if (Math.abs(dy) > DRAG_THRESHOLD_PX) detach()
          return
        }
        if (Math.abs(dy) <= DRAG_THRESHOLD_PX) return
        engaged = true
        clearHold()
        paint(moveEvent.clientY)
        return
      }
      // Only once engaged: preventing default any earlier would kill the page
      // scroll a non-drag touch is trying to perform.
      moveEvent.preventDefault()
      paint(moveEvent.clientY)
    }

    function onUp(upEvent: PointerEvent) {
      if (upEvent.pointerId !== pointerId) return
      const wasEngaged = engaged
      const target = wasEngaged ? resolve(upEvent.clientY) : null
      // The click the browser fires next must not also open the sheet.
      if (wasEngaged) suppressClick.current = true
      detach()
      if (target?.moved) latest.current.onDrop(target.startMinutes)
    }

    function onCancel(cancelEvent: PointerEvent) {
      if (cancelEvent.pointerId !== pointerId) return
      // The browser took the gesture over — a scroll started, or the tile
      // unmounted under the finger. Abandon; a cancel is never a drop.
      detach()
    }

    // Listeners on the WINDOW, not the tile: the pointer routinely leaves a
    // 220px-wide column mid-drag, and an element listener would stop receiving
    // moves the moment it did.
    window.addEventListener('pointermove', onMove, { passive: false })
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onCancel)

    if (isTouch) {
      holdTimer.current = setTimeout(() => {
        holdTimer.current = null
        engaged = true
        paint(startY)
      }, TOUCH_HOLD_MS)
    }
  }, [clearHold])

  const onClickCapture = useCallback(
    (event: { preventDefault: () => void; stopPropagation: () => void }) => {
      if (!suppressClick.current) return
      suppressClick.current = false
      event.preventDefault()
      event.stopPropagation()
    },
    [],
  )

  return { state, onPointerDown, onClickCapture }
}
